import { supabase } from "../lib/supabase";

interface MoneybirdResponse {
  success?: boolean;
  error?: string;
  message?: string;
  moneybird_invoice_id?: string;
  moneybird_invoice_url?: string;
  results?: {
    success: number;
    failed: number;
    updated: number;
    errors: string[];
  };
}

/**
 * Create an invoice in Moneybird
 */
export const createMoneybirdInvoice = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'create-moneybird-invoice',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling create-moneybird-invoice function:', error);
      return {
        data: null,
        error: error.message || 'Failed to create Moneybird invoice',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling create-moneybird-invoice:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Update an existing invoice in Moneybird
 */
export const updateMoneybirdInvoice = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'update-moneybird-invoice',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling update-moneybird-invoice function:', error);
      return {
        data: null,
        error: error.message || 'Failed to update Moneybird invoice',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling update-moneybird-invoice:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Sync payment status from Moneybird for a specific invoice
 */
export const syncMoneybirdStatus = async (invoiceId: number): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'sync-moneybird-status',
      {
        body: { invoice_id: invoiceId },
      }
    );

    if (error) {
      console.error('Error calling sync-moneybird-status function:', error);
      return {
        data: null,
        error: error.message || 'Failed to sync Moneybird status',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling sync-moneybird-status:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Sync payment status from Moneybird for all synced invoices
 */
export const syncAllMoneybirdStatus = async (): Promise<{
  data: MoneybirdResponse | null;
  error: string | null;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'sync-moneybird-status',
      {
        body: { sync_all: true },
      }
    );

    if (error) {
      console.error('Error calling sync-moneybird-status function:', error);
      return {
        data: null,
        error: error.message || 'Failed to sync all Moneybird statuses',
      };
    }

    return {
      data: data as MoneybirdResponse,
      error: null,
    };
  } catch (error) {
    console.error('Unexpected error calling sync-moneybird-status:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}; 